import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserService } from '@cadriciel/core';
import { environment } from 'environments/environment';
import { ActiveRoleService } from './active-role.service';

/**
 * Guard de rôle basé sur le RÔLE ACTIF sélectionné (switch de rôle côté UI).
 *
 * Les rôles autorisés sont déclarés sur la route via `data.roles` (tableau de
 * codes). L'accès est accordé si le rôle actif de l'utilisateur figure parmi
 * eux ; sinon l'utilisateur est redirigé vers le tableau de bord.
 *
 * À combiner avec {@link AuthGuard} (qui gère l'authentification) :
 * `canActivate: [AuthGuard, activeRoleGuard]`.
 */
export const activeRoleGuard: CanActivateFn = (route) => {
    const userService = inject(UserService);
    const activeRoleService = inject(ActiveRoleService);
    const router = inject(Router);

    const required = (route.data['roles'] as string[] | undefined) ?? [];
    if (required.length === 0) return true;

    // Mode dev sans authentification : pas de profil chargé → on laisse passer (cf. AuthGuard).
    if (!(environment as any).orionUrl) return true;

    // Le profil utilisateur est chargé de façon asynchrone après l'authentification.
    // On attend qu'il soit disponible avant d'évaluer le rôle actif, pour éviter de
    // refuser l'accès lors d'une navigation directe (deep link) au premier chargement.
    const currentUser$ = toObservable(userService.currentUser);
    return firstValueFrom(
        currentUser$.pipe(
            filter((user) => !!user),
            take(1),
            map(() => {
                const active = activeRoleService.activeRole();
                if (active && required.includes(active)) {
                    return true;
                }
                return router.parseUrl('/dashboard');
            })
        )
    );
};
